import { Link, Navigate, useParams } from "react-router-dom";
import { useSEO } from "@/lib/useSEO";
import { webPage, breadcrumb } from "@/lib/jsonLd";
import { pickLang } from "@/data/stoicosCards";
import { findPhilosopher, SOURCE_LANGS, EVIDENCE_STATUS, AL_THEMES } from "@/data/ancientLibrary";
import { findSvfEntry, entriesForPhilosopher } from "@/data/svfEntries";
import { ENTRY_IMAGES } from "@/data/ancientImages";
import { AncientImageFigure } from "@/pages/ancient/AncientImageFigure";
import { AboutEditionNote, AlBreadcrumb, GREEK_FONT, useAlLabels } from "@/pages/ancient/common";
import { useT } from "@/i18n/LanguageContext";

const MODERN_LANGS = ["en", "nl", "de", "fr", "es"];

const Sect = ({ label, children, testId }) => (
  <section className="mt-14" data-testid={testId}>
    <h2 className="text-[11px] tracking-[0.3em] uppercase text-gold mb-5">{label}</h2>
    {children}
  </section>
);

export default function SvfEntryPage() {
  const { philosopher, slug } = useParams();
  const { setLang } = useT();
  const { lang, labels } = useAlLabels();
  const entry = findSvfEntry(slug);
  const p = findPhilosopher(philosopher);
  const valid = entry && p && entry.philosopher === philosopher;

  useSEO({
    title: valid ? entry.metaTitle : "SVF | StoicOs.ai",
    description: valid ? entry.metaDescription : "",
    canonical: valid ? `https://stoicos.ai/ancient-library/svf/${philosopher}/${entry.slug}` : "https://stoicos.ai/ancient-library/svf",
    jsonLd: valid ? [
      webPage({ name: `${entry.ref} — ${p.name.en}`, description: entry.metaDescription, url: `/ancient-library/svf/${philosopher}/${entry.slug}` }),
      breadcrumb([
        { name: "Home", url: "/" },
        { name: "Ancient Library", url: "/ancient-library" },
        { name: "SVF", url: "/ancient-library/svf" },
        { name: p.name.en, url: `/ancient-library/svf/${philosopher}` },
        { name: entry.ref, url: `/ancient-library/svf/${philosopher}/${entry.slug}` },
      ]),
    ] : [],
  });

  if (!valid) return <Navigate to="/ancient-library/svf" replace />;

  const srcLangName = pickLang(SOURCE_LANGS[entry.witness.sourceLang], lang);
  const availableLangs = MODERN_LANGS.filter((lg) => entry.translation[lg]);
  const activeTranslation = entry.translation[lang];
  const c = entry.commentary;
  const siblings = entriesForPhilosopher(philosopher).filter((e) => e.verified);
  const idx = siblings.findIndex((e) => e.slug === entry.slug);
  const prev = idx > 0 ? siblings[idx - 1] : null;
  const next = idx >= 0 && idx < siblings.length - 1 ? siblings[idx + 1] : null;

  return (
    <main data-testid="svf-entry-page" className="relative pt-32 md:pt-40 pb-32">
      <article className="max-w-3xl mx-auto px-6 md:px-10">
        <AlBreadcrumb items={[
          { to: "/", label: "Home" },
          { to: "/ancient-library", label: labels.library },
          { to: "/ancient-library/svf", label: "SVF" },
          { to: `/ancient-library/svf/${philosopher}`, label: pickLang(p.name, lang) },
          { label: entry.ref },
        ]} />

        <h1 data-testid="entry-ref" className="font-serif text-4xl sm:text-5xl text-gold tracking-tight">{entry.ref}</h1>

        {/* Identification block */}
        <dl className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 p-6 md:p-7 border border-white/10 bg-ink-elevated/40" data-testid="entry-identification">
          <div><dt className="text-[10px] tracking-[0.25em] uppercase text-fog/60">{labels.stoic}</dt><dd className="mt-1 text-bone font-reading">{pickLang(p.name, lang)}</dd></div>
          <div><dt className="text-[10px] tracking-[0.25em] uppercase text-fog/60">{labels.ancientSource}</dt><dd className="mt-1 text-bone font-reading">{entry.witness.author}, {entry.witness.work} — {entry.witness.locus}</dd></div>
          <div><dt className="text-[10px] tracking-[0.25em] uppercase text-fog/60">{labels.sourceLanguage}</dt><dd className="mt-1 text-bone font-reading" data-testid="entry-source-lang">{srcLangName}</dd></div>
          <div><dt className="text-[10px] tracking-[0.25em] uppercase text-fog/60">{labels.sourceStatus}</dt><dd className="mt-1 text-bone font-reading" data-testid="entry-status">{entry.status.map((st) => (EVIDENCE_STATUS[st] ? pickLang(EVIDENCE_STATUS[st], lang) : st)).join(" · ")}</dd></div>
        </dl>
        <div className="mt-4 flex flex-wrap gap-2">
          {entry.themes.map((th) => (
            <Link key={th} to={`/ancient-library/svf/${philosopher}`} className="px-3 py-1 text-[10px] tracking-[0.22em] uppercase border border-white/10 text-fog/70 hover:text-gold hover:border-gold/40 transition-colors">{pickLang(AL_THEMES[th], lang)}</Link>
          ))}
        </div>

        {/* ORIGINAL SOURCE */}
        <Sect label={`${labels.original} — ${srcLangName}`} testId="entry-original">
          {entry.ancientText ? (
            <blockquote lang={entry.witness.sourceLang} className="p-8 md:p-10 border border-gold/30 bg-ink-elevated/50 text-bone text-xl md:text-2xl leading-[1.9] whitespace-pre-line" style={GREEK_FONT}>
              {entry.ancientText}
            </blockquote>
          ) : (
            <p className="p-6 border border-white/10 text-fog/70 font-reading italic" data-testid="entry-original-pending">{labels.originalPending}</p>
          )}
        </Sect>

        {/* STOICOS TRANSLATION */}
        <Sect label={labels.translation} testId="entry-translation">
          <div className="flex flex-wrap gap-2 mb-5" role="tablist" aria-label={labels.translation}>
            {availableLangs.map((lg) => (
              <button key={lg} type="button" role="tab" aria-selected={lang === lg} data-testid={`entry-lang-${lg}`} onClick={() => setLang(lg)}
                className={`px-3.5 py-1.5 text-[11px] tracking-[0.22em] uppercase border transition-colors ${lang === lg ? "border-gold text-gold bg-gold/5" : "border-white/10 text-bone/70 hover:border-white/25"}`}>
                {lg.toUpperCase()}
              </button>
            ))}
          </div>
          {activeTranslation ? (
            <p className="text-bone/95 font-reading text-lg md:text-xl leading-[1.9]" data-testid="entry-translation-text">{activeTranslation}</p>
          ) : (
            <p className="text-fog/70 font-reading italic" data-testid="entry-translation-pending">{labels.translationPending}</p>
          )}
        </Sect>

        {/* TEXTUALLY ATTESTED VISUALIZATION (only for entries with an attested image) */}
        {ENTRY_IMAGES[entry.slug] && (
          <section className="mt-14" data-testid="entry-visualization">
            <AncientImageFigure img={ENTRY_IMAGES[entry.slug]} lang={lang} labels={labels} />
          </section>
        )}

        {/* CONTEXT */}
        <Sect label={labels.context} testId="entry-context">
          <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(c.context, lang)}</p>
          <p className="mt-4 text-fog/70 font-reading text-sm leading-[1.75]"><span className="text-[10px] tracking-[0.22em] uppercase text-fog/60 mr-2">{labels.sourceNotes}:</span>{pickLang(entry.witness.notes, lang)}</p>
        </Sect>

        {/* WHAT THE SOURCE ACTUALLY TELLS US */}
        <Sect label={labels.evidence} testId="entry-evidence">
          <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(c.evidence, lang)}</p>
        </Sect>

        {/* PHILOSOPHICAL MEANING */}
        <Sect label={labels.meaning} testId="entry-meaning">
          <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(c.meaning, lang)}</p>
        </Sect>

        {/* UNCERTAINTY */}
        {c.uncertainty && (
          <Sect label={labels.uncertainty} testId="entry-uncertainty">
            <p className="p-6 border border-white/10 bg-ink-elevated/30 text-fog/85 font-reading text-sm md:text-base leading-[1.8]">{pickLang(c.uncertainty, lang)}</p>
          </Sect>
        )}

        {/* PRACTICAL WISDOM */}
        {c.practical && (
          <Sect label={labels.practical} testId="entry-practical">
            <p className="text-bone/85 font-reading text-base md:text-lg leading-[1.85]">{pickLang(c.practical, lang)}</p>
          </Sect>
        )}

        {/* BIOGRAPHICAL CONTEXT — links into the Zeno Vita */}
        {entry.links.some((l) => l.to.includes("/vita")) && (
          <Sect label={labels.bioContext} testId="entry-bio-context">
            <div className="p-5 border border-gold/25 bg-ink-elevated/30">
              <ul className="space-y-2">
                {entry.links.filter((l) => l.to.includes("/vita")).map((l, i) => (
                  <li key={i}>
                    <Link to={l.to} className="text-gold hover:underline font-reading text-base md:text-lg" data-testid={`entry-vita-link-${i}`}>
                      {pickLang(l.label, lang)} →
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </Sect>
        )}

        {/* Internal links */}
        {entry.links.some((l) => !l.to.includes("/vita")) && (
        <Sect label={labels.deeper} testId="entry-links">
          <ul className="space-y-2">
            {entry.links.filter((l) => !l.to.includes("/vita")).map((l, i) => (
              <li key={i}><Link to={l.to} className="text-gold hover:underline font-reading text-base md:text-lg" data-testid={`entry-link-${i}`}>{pickLang(l.label, lang)} →</Link></li>
            ))}
          </ul>
        </Sect>
        )}

        {(prev || next) && (
          <nav className="mt-14 pt-6 border-t border-white/10 flex justify-between gap-4">
            {prev ? <Link to={`/ancient-library/svf/${philosopher}/${prev.slug}`} className="text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors" data-testid="entry-prev">← {labels.prevEntry}: {prev.ref}</Link> : <span />}
            {next && <Link to={`/ancient-library/svf/${philosopher}/${next.slug}`} className="text-[11px] tracking-[0.22em] uppercase text-fog/70 hover:text-gold transition-colors" data-testid="entry-next">{labels.nextEntry}: {next.ref} →</Link>}
          </nav>
        )}

        <AboutEditionNote />
      </article>
    </main>
  );
}
